<template>
  <div>
    <a-toolbar class="primary-gradient pl-4">
      <v-icon left dark> mdi-label-variant </v-icon
      ><span>{{ $t("home.lblAnnouncement") }}</span>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="$security.checkUserAccess('CreateAnnouncement')"
            v-bind="attrs"
            v-on="on"
            icon
            small
            class="mr-2"
            @click="openAnnouncementDialog"
          >
            <v-icon color="white">mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("home.addAnnouncement") }}</span>
      </v-tooltip>
    </a-toolbar>
    <v-card color="Neutral">
      <v-data-iterator
        :items="announcements"
        :items-per-page.sync="itemsPerPage"
        :page.sync="page"
        :no-data-text="$t('noData')"
        :footer-props="{
          'items-per-page-text': $t('lblRowsPerPage'),
        }"
      >
        <template v-slot:default="props">
          <v-list class="news-list" color="Neutral">
            <template v-for="item in props.items">
              <v-list-item
                :key="item.id"
                class="mb-1 bg-red white"
                @click="onView(item.id)"
              >
                <span class="label-m ml-1" v-if="item.created_date">{{
                  formatDate(new Date(item.created_date), "MMM dd yyyy")
                }}</span>
                <span v-else class="label-m ml-1">Not found</span>
                <v-divider :vertical="true" class="mr-3 ml-3" inset />
                <v-list-item-content>
                  <v-list-item-title class="text-left">{{
                    item.title
                  }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-row no-gutters>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-if="$security.checkUserAccess('ViewAnnouncement')"
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="onView(item.id)"
                          x-small
                        >
                          <v-icon size="16" color="neutralGray1">mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("home.tipViewAnnouncement") }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-if="$security.checkUserAccess('UpdateAnnouncement')"
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="onEdit(item.id)"
                          x-small
                        >
                          <v-icon size="16" color="neutralGray1">mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("home.tipUpdateAnnouncement") }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-if="$security.checkUserAccess('DeleteAnnouncement')"
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="confirmDelete(item.id)"
                          x-small
                        >
                          <v-icon size="16" color="neutralGray1">mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("home.tipDeleteAnnouncement") }}</span>
                    </v-tooltip>
                  </v-row>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </template>
      </v-data-iterator>
      <confirm-dialog ref="confirmDelete" />
      <add-announcement
        v-if="announcementDialog"
        v-model="announcementDialog"
        :announcementForm="announcementForm"
        @alert="onAlertMessage"
        :isEdit="updateMode"
      />
      <view-announcement
        v-if="viewAnnouncementDialog"
        v-model="viewAnnouncementDialog"
        :announcementForm="announcementForm"
      />
      <message-sheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></message-sheet>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ConfirmDialog from "../ConfirmDialog.vue";
import { formatDate } from "../../utils/util";
import { cloneDeep } from "lodash";
import AddAnnouncement from "./AddAnnouncement.vue";
import ViewAnnouncement from "./ViewAnnouncement.vue";
import AToolbar from "../../wrapper-component/AToolbar.vue";
import MessageSheet from "../MessageSheet.vue";
import SignalRMixin from "../../mixin/SignalRMixin";

export default {
  name: "Announcement",
  mixins: [SignalRMixin],
  components: {
    ConfirmDialog,
    AddAnnouncement,
    ViewAnnouncement,
    AToolbar,
    MessageSheet,
  },
  data() {
    return {
      updateMode: false,
      displayMessage: false,
      message: "",
      messageType: "",
      announcementForm: {},
      refAnnouncementForm: {
        id: null,
        tenantid: null,
        created_date: new Date().toISOString(),
        title: "",
        content: "",
        documents: [],
        created_by: this.$auth.user.email,
      },
      showTextEditor: false,
      announcementDialog: false,
      viewAnnouncementDialog: false,
      page: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    ...mapState({
      announcementList: (state) => state.news.announcementList,
    }),
    LoaderText() {
      return this.$t("LoaderText");
    },
    announcements() {
      if (this.announcementList.length) {
        return this.announcementList.sort(function (a, b) {
          return new Date(b.created_date) - new Date(a.created_date);
        });
      }
      return [];
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
  },
  async mounted() {
    if (this.$security.checkUserAccess("ViewAnnouncement")) {
      await this.getAnnouncementList();
      const target = "Announcement_" + this.selectedTenant;
      await this.signalRInit(target);
    }
  },
  methods: {
    ...mapActions({
      deleteAnnouncement: "news/deleteAnnouncement",
      getAnnouncementById: "news/getAnnouncementById",
      getAnnouncementList: "news/getAnnouncementList",
    }),
    confirmDelete,
    onEdit,
    onView,
    formatDate,
    openAnnouncementDialog,
    onAlertMessage,
    showMessageSheet,
    signalRInit,
  },
};

async function signalRInit(target) {
  try {
    this.signalConnection.on(target, async (res) => {
      if (res) {
        try {
          await this.getAnnouncementList();
        } catch (error) {
          console.log(error, "API error");
        }
      }
    });
    if (
      this.signalConnection.connectionState.toLowerCase() === "disconnected"
    ) {
      this.signalConnection.start().catch();
    }
  } catch (error) {}
}

function onAlertMessage(message, MessageType) {
  this.showMessageSheet(message, MessageType);
}

function showMessageSheet(message, messageType) {
  this.displayMessage = true;
  this.message = message;
  this.messageType = messageType;
}

function openAnnouncementDialog() {
  this.updateMode = false;
  this.announcementForm = cloneDeep(this.refAnnouncementForm);
  this.announcementDialog = true;
}

async function onEdit(id) {
  try {
    this.updateMode = true;
    let data = await this.getAnnouncementById(id);
    data.documents = (data.documents?.length ? data.documents.map(i => ({ ...i, isUploaded: true })) : []);
    this.announcementForm = cloneDeep(data);
    this.announcementDialog = true;
  } catch (error) {
    console.log(error, "api error");
  }
}

async function onView(id) {
  try {
    const data = await this.getAnnouncementById(id);
    data.documents = data?.documents || []
    this.announcementForm = cloneDeep(data);
    this.viewAnnouncementDialog = true;
  } catch (error) {
    console.log(error, "api error");
  }
}

async function confirmDelete(id) {
  try {
    if (
      await this.$refs.confirmDelete.open(
        this.$t("agenda.hDeleteMessage"),
        this.$t("agenda.lblDeleteMessage")
      )
    ) {
      await this.deleteAnnouncement(id);
      this.showMessageSheet(
        this.$t("home.announcementDeletionSuccess"),
        "success"
      );
    }
  } catch (error) {
    console.log(error, "api error");
  }
}
</script>

<style scoped>
.news-list {
  overflow-y: auto;
  padding-top: 0px !important;
}
</style>